import React, { useEffect, useMemo, useState } from 'react';
import { _T } from '../../../include/appI18n';
const placeholderRegex = /\{(\d+)\}/g;
function normalizeRemarkText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
}
function splitRemarkLines(value) {
    return (value || '')
        .split(/\r?\n/)
        .map(x => x.trim())
        .filter(x => !!x);
}
function escapeRegexLiteral(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
function escapeTemplateLiteral(value) {
    let result = '';
    let lastWasWhitespace = false;
    for (const ch of value) {
        if (/\s/.test(ch)) {
            if (!lastWasWhitespace)
                result += '\\s+';
            lastWasWhitespace = true;
            continue;
        }
        result += escapeRegexLiteral(ch);
        lastWasWhitespace = false;
    }
    return result;
}
function getTemplatePlaceholders(template) {
    const matches = [];
    template.replace(placeholderRegex, (match, placeholderIndex, index) => {
        matches.push({ index, length: match.length, placeholderIndex: Number(placeholderIndex) });
        return match;
    });
    return matches;
}
function buildTemplateParts(template, placeholders) {
    const parts = [];
    let position = 0;
    for (const placeholder of placeholders) {
        if (placeholder.index > position)
            parts.push({ text: template.substring(position, placeholder.index) });
        parts.push({ text: '', placeholderIndex: placeholder.placeholderIndex });
        position = placeholder.index + placeholder.length;
    }
    if (position < template.length)
        parts.push({ text: template.substring(position) });
    if (!parts.length)
        parts.push({ text: template });
    return parts;
}
function buildTemplatePattern(template, placeholders) {
    let pattern = '^';
    let position = 0;
    for (const placeholder of placeholders) {
        pattern += escapeTemplateLiteral(template.substring(position, placeholder.index));
        pattern += `(?<p${placeholder.placeholderIndex}>.*?)`;
        position = placeholder.index + placeholder.length;
    }
    pattern += escapeTemplateLiteral(template.substring(position));
    pattern += '$';
    return new RegExp(pattern, 'i');
}
function parseRemarkTemplates(remarkTemplates, additionalRequirements) {
    const customRemarkLines = splitRemarkLines(additionalRequirements);
    const templates = (remarkTemplates || []).filter(x => !!x && !!x.trim());
    const templateStates = templates.map(template => {
        var _a;
        const placeholders = getTemplatePlaceholders(template);
        const parts = buildTemplateParts(template, placeholders);
        const placeholderIndexes = Array.from(new Set(placeholders.map(x => x.placeholderIndex)));
        const templatePattern = placeholders.length ? buildTemplatePattern(template, placeholders) : null;
        const values = {};
        let matchedIndex = -1;
        for (let i = 0; i < customRemarkLines.length; i++) {
            const line = customRemarkLines[i];
            if (!placeholders.length && line.toLowerCase() === template.toLowerCase()) {
                matchedIndex = i;
                break;
            }
            if (templatePattern) {
                const match = templatePattern.exec(line);
                if (match) {
                    const matchedValues = {};
                    for (const placeholderIndex of placeholderIndexes)
                        matchedValues[placeholderIndex] = (((_a = match.groups) === null || _a === void 0 ? void 0 : _a[`p${placeholderIndex}`]) || '').trim();
                    if (placeholderIndexes.some(placeholderIndex => !matchedValues[placeholderIndex]))
                        continue;
                    matchedIndex = i;
                    for (const placeholderIndex of placeholderIndexes)
                        values[placeholderIndex] = matchedValues[placeholderIndex];
                    break;
                }
            }
        }
        if (matchedIndex >= 0)
            customRemarkLines.splice(matchedIndex, 1);
        return {
            template,
            checked: matchedIndex >= 0,
            values,
            parts,
        };
    });
    return {
        templateStates,
        customText: customRemarkLines.join('\n'),
    };
}
function getTemplateText(template) {
    const placeholderIndexes = Array.from(new Set(template.parts.map(x => x.placeholderIndex).filter((x) => x !== undefined)));
    if (placeholderIndexes.some(placeholderIndex => !normalizeRemarkText(template.values[placeholderIndex])))
        return '';
    return normalizeRemarkText(template.template.replace(placeholderRegex, (match, placeholderIndex) => template.values[Number(placeholderIndex)] || ''));
}
function buildAdditionalRequirements(templateStates, customText) {
    const remarks = templateStates
        .filter(x => x.checked)
        .map(getTemplateText)
        .filter(x => !!x);
    const normalizedCustomText = customText.trim();
    if (normalizedCustomText)
        remarks.push(normalizedCustomText);
    return remarks.join('\n');
}
export function BookingRemarkTemplates(props) {
    const parsed = useMemo(() => parseRemarkTemplates(props.remarkTemplates, props.additionalRequirements), [props.additionalRequirements, props.remarkTemplates]);
    const [templateStates, setTemplateStates] = useState(parsed.templateStates);
    const [customText, setCustomText] = useState(parsed.customText);
    useEffect(() => {
        setTemplateStates(parsed.templateStates);
        setCustomText(parsed.customText);
    }, [parsed]);
    const applyChange = (nextTemplateStates, nextCustomText) => {
        setTemplateStates(nextTemplateStates);
        setCustomText(nextCustomText);
        props.onChange(buildAdditionalRequirements(nextTemplateStates, nextCustomText));
    };
    const setTemplateChecked = (templateIndex, checked) => {
        const nextTemplateStates = templateStates.map((template, index) => index === templateIndex ? Object.assign(Object.assign({}, template), { checked }) : template);
        applyChange(nextTemplateStates, customText);
    };
    const setTemplateValue = (templateIndex, placeholderIndex, value) => {
        const nextTemplateStates = templateStates.map((template, index) => index === templateIndex ? Object.assign(Object.assign({}, template), { values: Object.assign(Object.assign({}, template.values), { [placeholderIndex]: value }) }) : template);
        applyChange(nextTemplateStates, customText);
    };
    return (React.createElement("div", { className: "hotel-body my-4" },
        React.createElement("div", { className: "arrived booking-remark", id: "booking-add-req" },
            React.createElement("h2", null, _T.COMMON_REMARK_FOR_BOOKING),
            React.createElement("textarea", { className: "add-req hidden", rows: 1, value: buildAdditionalRequirements(templateStates, customText), readOnly: true }),
            React.createElement("div", { className: "row" },
                React.createElement("div", { className: "col-xs-12 col-md-6" },
                    React.createElement("div", { className: "booking-remark-template-list" }, templateStates.map((template, templateIndex) => (React.createElement("div", { key: templateIndex, className: `checkbox booking-remark-template-row ${template.checked ? 'active' : ''}` },
                        React.createElement("label", { htmlFor: `bookingRemark_${templateIndex}`, className: "booking-remark-template-label" },
                            React.createElement("input", { type: "checkbox", id: `bookingRemark_${templateIndex}`, className: "booking-remark-template", "data-template": template.template, checked: template.checked, onChange: e => setTemplateChecked(templateIndex, e.target.checked) }),
                            React.createElement("span", { className: "booking-remark-template-content" }, template.parts.map((part, partIndex) => part.placeholderIndex === undefined ? (part.text ? React.createElement("span", { key: partIndex, className: "booking-remark-template-text" }, part.text) : null) : (React.createElement("input", { key: partIndex, type: "text", className: "form-control booking-remark-template-value", "data-placeholder-index": part.placeholderIndex, maxLength: 50, value: template.values[part.placeholderIndex] || '', disabled: !template.checked, onClick: e => e.stopPropagation(), onMouseDown: e => e.stopPropagation(), onChange: e => setTemplateValue(templateIndex, part.placeholderIndex, e.target.value) })))))))))),
                React.createElement("div", { className: "col-xs-12 col-md-6" },
                    React.createElement("textarea", { className: "form-control booking-remark-custom w-100", maxLength: 250, rows: 8, cols: 4, value: customText, onChange: e => applyChange(templateStates, e.target.value) }),
                    React.createElement("p", null,
                        "* ",
                        _T.COMMON_ADDITIONAL_REQUIREMENTS_COMMENT))))));
}
